import React from 'react';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ButtonInc from '../../shared-ui/ButtonInc';
import { RecentPoliciesHeader, DisplayRecentPolicies } from '../../components/RecentPolicies';
import { getRecentPoliciesRequest } from '../../components/RecentPolicies/Redux/actions';
import { getPolicyMakerChecksRequest } from '../PartnerStatus/actions.es6';
import SkeletonWrapper from '../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import { getPartnerGuid } from '../../lib/access.es6';
import PageNotFound from '../PageNotFound/index.es6';
import {
  buildApprovalStages,
  canViewApprovalStage,
  findApprovalStage,
  approvalStageTabs
} from './stages';

const DATE_DISCRIMINATORS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'start_date', label: 'Start Date' },
  { value: 'due_date', label: 'Due Date' }
];

function DateFilters(props) {
  const defaultDateBy = props.defaultDateBy || 'created_at';
  const [inputs, setInputs] = React.useState({
    dateBy: defaultDateBy,
    fromDate: '',
    toDate: ''
  });

  React.useEffect(function() {
    setInputs({ dateBy: props.defaultDateBy || 'created_at', fromDate: '', toDate: '' });
  }, [props.tabKey, props.defaultDateBy]);

  const setField = function(field) {
    return function(e) {
      const val = e.target.value;
      setInputs(function(prev) { return { ...prev, [field]: val }; });
    };
  };

  const invalidRange = !!inputs.fromDate && !!inputs.toDate && inputs.fromDate > inputs.toDate;

  const handleApply = function() {
    const params = {};
    if (inputs.fromDate) params.from = `${inputs.fromDate}T00:00:00.000Z`;
    if (inputs.toDate) params.to = `${inputs.toDate}T23:59:59.999Z`;
    if (inputs.fromDate || inputs.toDate) params.date_discriminator = inputs.dateBy;
    props.onApply(params);
  };

  const handleClear = function() {
    setInputs({ dateBy: defaultDateBy, fromDate: '', toDate: '' });
    props.onApply({});
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
      <TextField
        select
        size="small"
        label="Filter Date By"
        value={inputs.dateBy}
        onChange={setField('dateBy')}
        sx={{ flex: 1, minWidth: 140 }}
      >
        {DATE_DISCRIMINATORS.map(function(opt) {
          return (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          );
        })}
      </TextField>
      <TextField
        size="small"
        type="date"
        label="From Date"
        value={inputs.fromDate}
        onChange={setField('fromDate')}
        error={invalidRange}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: 1, minWidth: 140 }}
      />
      <TextField
        size="small"
        type="date"
        label="To Date"
        value={inputs.toDate}
        onChange={setField('toDate')}
        error={invalidRange}
        helperText={invalidRange ? 'From must be on or before To' : ''}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: 1, minWidth: 140 }}
      />
      <ButtonInc
        variant="contained"
        onClick={handleApply}
        disabled={invalidRange || props.disabled}
        sx={{ textTransform: 'none' }}
      >
        Apply Filters
      </ButtonInc>
      <ButtonInc
        variant="outlined"
        onClick={handleClear}
        disabled={props.disabled}
        sx={{ textTransform: 'none' }}
      >
        Clear
      </ButtonInc>
    </Box>
  );
}

// Maker-checker approval dashboard. One route param (:stage) selects which of
// the partner's configured approval levels this instance serves — see
// ./stages.js, which builds the level list from the partner's maker-check
// configuration.
//
// Each tab is a server-paginated query filtered by maker_check_label and
// maker_check_status. Date range and policy-number filters narrow the server
// query; makerStage is applied client-side on the returned page to exclude
// policies that are queued behind an earlier stage.
class PolicyApprovalDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { tabValue: 0, dateFilter: {}, searchTerm: '' };
  }

  componentDidMount() {
    if (!this.props.makerChecksLoaded && !this.props.makerChecksLoading) {
      this.props.dispatch(getPolicyMakerChecksRequest());
    }
    this.fetch(this.currentTab());
  }

  componentDidUpdate(prevProps) {
    const stageChanged =
      (prevProps.params && prevProps.params.stage) !==
      (this.props.params && this.props.params.stage);
    if (stageChanged) {
      this.setState({ tabValue: 0, dateFilter: {}, searchTerm: '' }, () => {
        this.lastQuery = null;
        this.fetch(this.currentTab());
      });
      return;
    }
    // Re-fetch only when makerChecks finish loading — the configured labels may
    // differ from the fallback used on mount, changing the tab query. Tab
    // switches, filters and pagination have their own handlers.
    if (!prevProps.makerChecksLoaded && this.props.makerChecksLoaded) {
      this.fetch(this.currentTab());
    }
  }

  stages() {
    return buildApprovalStages({
      makerChecks: this.props.makerChecks,
      loaded: this.props.makerChecksLoaded,
      partnerGuid: getPartnerGuid()
    });
  }

  stage() {
    return findApprovalStage(
      this.props.params && this.props.params.stage,
      this.stages()
    );
  }

  tabs() {
    return approvalStageTabs(this.stage());
  }

  currentTab() {
    const tabs = this.tabs();
    return tabs[this.state.tabValue] || tabs[0] || null;
  }

  fetch(tab, payload = {}) {
    if (!tab) return;
    const request = {
      page: 1,
      size: 10,
      ...tab.query,
      ...this.state.dateFilter,
      ...(this.state.searchTerm ? { policy_number: this.state.searchTerm } : {}),
      ...payload
    };
    const signature = JSON.stringify(request);
    const failed = !!(this.props.recentPoliciesData || {}).errors;
    if (signature === this.lastQuery && !failed) return;
    this.lastQuery = signature;
    this.props.dispatch(getRecentPoliciesRequest(request));
  }

  handleTabChange(event, newValue) {
    this.setState({ tabValue: newValue, dateFilter: {}, searchTerm: '' }, () => {
      this.lastQuery = null;
      this.fetch(this.currentTab());
    });
  }

  handleDateFilterApply(dateParams) {
    this.setState({ dateFilter: dateParams }, () => {
      this.fetch(this.currentTab(), { page: 1, size: 10 });
    });
  }

  handleSearch(value) {
    this.setState({ searchTerm: value }, () => {
      this.fetch(this.currentTab(), { page: 1, size: 10 });
    });
  }

  handleNextPage(payload = {}) {
    this.fetch(this.currentTab(), payload);
  }

  render() {
    const stage = this.stage();

    if (!stage) {
      const settled = this.props.makerChecksLoaded || !!this.props.makerChecksError;
      return settled ? <PageNotFound /> : <SkeletonWrapper loading />;
    }

    if (!canViewApprovalStage(stage, this.stages())) {
      return <PageNotFound />;
    }

    const tabs = this.tabs();
    const tabValue = tabs[this.state.tabValue] ? this.state.tabValue : 0;
    const tab = tabs[tabValue];
    const { recentPoliciesData } = this.props;
    const {
      policies = [],
      loader = false,
      page = 1,
      size = 20,
      total = 0
    } = recentPoliciesData || {};

    return (
      <>
        <RecentPoliciesHeader title={stage.title} />

        {loader && !policies.length ? (
          <SkeletonWrapper loading={loader} />
        ) : (
          <section style={{ padding: '20px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={(event, newValue) => this.handleTabChange(event, newValue)}
                    aria-label={`${stage.title} tabs`}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {tabs.map((entry) => (
                      <Tab key={entry.key} label={entry.label} />
                    ))}
                  </Tabs>
                </Box>

                <DateFilters
                  tabKey={tab.key}
                  onApply={(params) => this.handleDateFilterApply(params)}
                  disabled={loader}
                />

                {loader ? <LinearProgress sx={{ mb: 1 }} /> : null}

                <DisplayRecentPolicies
                  key={`${stage.key}-${tab.key}`}
                  policies={policies}
                  handleNextPage={(payload) => this.handleNextPage(payload)}
                  loader={loader}
                  page={page}
                  size={size}
                  total={total}
                  needAction={tab.needAction}
                  makerStage={tab.makerStage}
                  decisionStage={tab.decisionStage}
                  showStage
                  emptyMessage={tab.emptyMessage}
                  searchValue={this.state.searchTerm}
                  onSearch={(value) => this.handleSearch(value)}
                />
              </Grid>
            </Grid>
          </section>
        )}
      </>
    );
  }
}

export default connect((state) => ({
  recentPoliciesData: state.recentPoliciesData,
  makerChecks: state.partnerStatus.policyMakerChecks,
  makerChecksLoaded: state.partnerStatus.policyMakerChecksLoaded,
  makerChecksLoading: state.partnerStatus.policyMakerChecksLoader,
  makerChecksError: state.partnerStatus.policyMakerChecksError
}))(PolicyApprovalDashboard);
